import { isGranted, type ErrorCode, type PermissionPath } from "core";

import type { IdentityRole } from "core";
import { useAuthContext } from "./useAuthContext";

/**
 * Checks if user has a specific permission based on static, hardcoded permission rules.
 *
 * This hook evaluates permissions from a static permission file (not configurable).
 * Use this for core system permissions that don't change per project or user settings.
 *
 * For dynamic, configurable feature permissions, use `useFeatureAccess()` hook instead.
 */
export function useIsGranted({
  defaultRole,
  permission,
}: {
  defaultRole?: IdentityRole;
  permission: PermissionPath;
  redirect?: ErrorCode;
}) {
  const { identity } = useAuthContext();

  if (!defaultRole && !identity?.primaryRole) {
    return {
      hasPermission: false,
      identity,
    };
  }

  const identityRole = defaultRole ?? identity?.primaryRole;
  const { hasPermission } = isGranted({ identityRole, permission });

  return {
    hasPermission,
    identity,
  };
}
